import { ConstExpressionModel } from './ConstExpressionModel';
import { ExpressionModel } from './ExpressionModel';
import { ExpressionTypes } from './ExpressionTypes';
export { TEMPLATE_CONTENT_PLACEHOLDER_ID, TEMPLATE_CONTENT_PLACEHOLDER_TOKEN, composeTemplateContent, isSystemTemplatePlaceholder } from './TemplatePlaceholderRules';

export type StringTemplateFormat = 'Text' | 'Html' | 'Markdown';

export const TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE = 'application/x-template-placeholder';

export type StringTemplateExpressionModel = ExpressionModel & {
	Type: typeof ExpressionTypes.StringTemplate;
	Format: StringTemplateFormat;
	Content: string;
	Placeholders: Record<string, ExpressionModel>;
};

export function isStringTemplateModel(expression: ExpressionModel | undefined): expression is StringTemplateExpressionModel {
	return expression?.Type === ExpressionTypes.StringTemplate;
}

/**
 * Coerces a legacy plain-string value to a StringTemplateModel.
 * If `expression` is already a StringTemplateModel it is returned as-is.
 * If it is a raw string (e.g. a property previously stored without a type wrapper)
 * it is wrapped into a new StringTemplateModel with no placeholders.
 * Otherwise returns `undefined`.
 */
export function coerceToStringTemplate(
	expression: ExpressionModel | string | undefined,
	format: StringTemplateFormat,
): StringTemplateExpressionModel | undefined {
	if (isStringTemplateModel(expression as ExpressionModel)) {
		const template = expression as StringTemplateExpressionModel;
		return {
			...template,
			Format: normalizeStringTemplateFormat(template.Format),
		};
	}
	if (typeof expression === 'string' && expression.length > 0) {
		return {
			Type: ExpressionTypes.StringTemplate,
			Format: format,
			Content: expression,
			Placeholders: {},
		};
	}
	const asConst = expression as ConstExpressionModel | undefined;
	if (asConst?.Type === ExpressionTypes.Const && typeof asConst.Value === 'string') {
		return {
			Type: ExpressionTypes.StringTemplate,
			Format: format,
			Content: asConst.Value,
			Placeholders: {},
		};
	}
	return undefined;
}

export function normalizeStringTemplateFormat(format: string | undefined): StringTemplateFormat {
	if (format === 'HTML') {
		return 'Html';
	}

	if (format === 'Text' || format === 'Html' || format === 'Markdown') {
		return format;
	}

	return 'Text';
}

/** Matches `{{placeholderName}}` tokens — safe to reuse with `.replace()` (g flag resets lastIndex). */
export const PLACEHOLDER_REPLACE_REGEX = /\{\{\s*([A-Za-z_][A-Za-z0-9_.-]*|expr-\d+)\s*\}\}/g;

/** Matches a single `{{expr-N}}` token — no g flag, safe to reuse with `.match()`. */
export const PLACEHOLDER_MATCH_REGEX = /\{\{\s*([A-Za-z_][A-Za-z0-9_.-]*|expr-\d+)\s*\}\}/;

/** Extract unique placeholder IDs referenced in a content string (e.g. `{{expr-1}}`). */
export function extractPlaceholderIds(content: string): string[] {
	return Array.from(new Set(Array.from(content.matchAll(PLACEHOLDER_REPLACE_REGEX), (m) => m[1])));
}

export function getPlaceholderIdAtPosition(content: string, position: number): string | undefined {
	for (const match of Array.from(content.matchAll(PLACEHOLDER_REPLACE_REGEX))) {
		const start = match.index ?? -1;
		if (position >= start && position < start + match[0].length) {
			return match[1];
		}
	}

	return undefined;
}

/** Remove entries from `placeholders` whose IDs no longer appear in `content`. */
export function pruneOrphanedPlaceholders(content: string, placeholders: Record<string, ExpressionModel>): Record<string, ExpressionModel> {
	const referenced = new Set(extractPlaceholderIds(content));
	return Object.fromEntries(Object.entries(placeholders).filter(([id]) => referenced.has(id)));
}

/** Generate the next available placeholder ID given the current set of IDs. */
export function nextPlaceholderId(placeholders: Record<string, ExpressionModel>): string {
	const existing = Object.keys(placeholders)
		.map((k) => parseInt(k.replace('expr-', ''), 10))
		.filter((n) => !isNaN(n));
	const next = existing.length > 0 ? Math.max(0, ...existing) + 1 : 1;
	return `expr-${next}`;
}
